import { c as _c } from "react/compiler-runtime";
import { MessageCircleWarning } from 'lucide-react';
import { memo, useCallback } from 'react';
import { useMeasuredKeyFromStore, useMeasureActions } from '@/hooks/store/use-measure-store';
import { useMeasureOptions } from '@/hooks/store/use-measure-options-store';
import { useTranslation } from '@/hooks/use-translation';
import { formatRatio } from '@/lib/format/format-weight';
import type { MeasuredKeyRequirements, OptionalNumber } from '@/lib/piano/touch-design/measured-key.requirements';
import { cn } from '@/lib/utils';
import { useMeasurePasses, type MeasurePassId } from './hooks/use-measure-passes';
import { MeasureInputField } from './MeasureInputField';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { useKeyMeasurementInconsistency } from './hooks/use-key-measurement-inconsistency';
type MeasuredProperty = keyof Pick<MeasuredKeyRequirements, 'downWeightWithSpringSupport' | 'downWeightWithoutSpringSupport' | 'frontWeight' | 'strikeWeight' | 'upWeight' | 'measuredStrikeWeightRatio'>;
export interface KeyMeasurementProps {
  currentPassId: MeasurePassId;
  isBlackKey: boolean;
  /** The key being filled, tinted as the mockup tints it. */
  isCurrent: boolean;
  keyName: string;
  keyNumber: number;
}
const cellClassName = 'h-[42px] border-b border-border px-3 align-middle';

/*
 * Below 1280px the table scrolls sideways in its frame, and once it has, the
 * front weight of which key? The number and the note stay put, on the row's
 * own background so the fields slide under them.
 */
const keyCellClassName = 'max-xl:sticky max-xl:z-[1] max-xl:bg-[inherit]';

/**
 * One key of the measure table: the fields to fill.
 *
 * Memoised: a Tab moves the focus, and with it the key the panel above
 * follows, but nothing a row shows — eighty-eight rows of four or six fields
 * have no business re-rendering for it.
 */
export const KeyMeasurement = memo(function KeyMeasurement(t0) {
  const $ = _c(24);
  const {
    currentPassId,
    isBlackKey,
    isCurrent,
    keyName,
    keyNumber
  } = t0;
  const keyIndex = keyNumber - 1;
  const keySpec = useMeasuredKeyFromStore(keyIndex);
  const {
    updateKeyMeasure
  } = useMeasureActions();
  const {
    useManualSWRMeasurements,
    useSupportSpringMeasurements
  } = useMeasureOptions();
  const {
    currentLanguage,
    t
  } = useTranslation();
  const {
    allowedDifference,
    computedStrikeWeightRatio,
    isKeyMeasurementInconsistent
  } = useKeyMeasurementInconsistency(keyNumber);
  const onUpdateKeyProperty = keyProperty => value => {
    updateKeyMeasure(keyIndex, keyProperty, value);
  };
  const columns = [...(useSupportSpringMeasurements ? [{
    label: "DS",
    property: "downWeightWithSpringSupport" as const
  }] : []), {
    label: "D",
    property: "downWeightWithoutSpringSupport"
  }, {
    label: "U",
    property: "upWeight"
  }, {
    label: "FW",
    property: "frontWeight"
  }, {
    label: "SW",
    property: "strikeWeight"
  }, ...(useManualSWRMeasurements ? [{
    label: "SWR",
    property: "measuredStrikeWeightRatio" as const
  }] : [])];
  const passes = useMeasurePasses();
  const activeFields = passes.find(pass => pass.id === currentPassId)?.fields ?? [];
  if (!keySpec) {
    return null;
  }
  const isUpAboveDown = keySpec.upWeight !== null && keySpec.downWeightWithoutSpringSupport !== null && keySpec.upWeight > keySpec.downWeightWithoutSpringSupport;
  const t1 = `measure-key-${keyNumber}`;
  const t2 = isCurrent ? "true" : undefined;
  const t3 = isCurrent ? "bg-[color-mix(in_oklab,var(--ptl-current)_7%,var(--card))] dark:bg-[color-mix(in_oklab,var(--ptl-current)_10%,var(--card))]" : "bg-card even:bg-secondary";
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn(cellClassName, keyCellClassName, "font-mono text-xs text-ptl-ink-3 max-xl:left-0");
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  let t5;
  if ($[1] !== allowedDifference || $[2] !== computedStrikeWeightRatio || $[3] !== currentLanguage || $[4] !== isKeyMeasurementInconsistent || $[5] !== t) {
    t5 = isKeyMeasurementInconsistent && <Tooltip><TooltipTrigger aria-label={t("Inconsistent measurement")}><MessageCircleWarning className="size-4 text-destructive" /></TooltipTrigger><TooltipContent side="right" variant="destructive"><p className="whitespace-pre-line">{t("The measured SWR is not consistent with the computed SWR ({{computedStrikeWeightRatio}}). The difference is greater than {{difference}}.\nYou should check your measurements to find the source of the inconsistency.\nCheck the SWR chart on analyze page for more details.", {
            computedStrikeWeightRatio: formatRatio(computedStrikeWeightRatio ?? null, currentLanguage),
            difference: formatRatio(allowedDifference, currentLanguage)
          })}</p></TooltipContent></Tooltip>;
    $[1] = allowedDifference;
    $[2] = computedStrikeWeightRatio;
    $[3] = currentLanguage;
    $[4] = isKeyMeasurementInconsistent;
    $[5] = t;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== keyNumber || $[8] !== t5) {
    t6 = <td className={t4}><span className="inline-flex items-center gap-1.5">{keyNumber}{t5}</span></td>;
    $[7] = keyNumber;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn(cellClassName, keyCellClassName, "font-mono text-[13px] max-xl:left-[52px]");
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== isBlackKey || $[12] !== keyName) {
    t8 = isBlackKey ? <span className="inline-flex rounded-[3px] bg-ptl-key-black px-1.5 py-px text-[12.5px] text-ptl-key-white">{keyName}</span> : keyName;
    $[11] = isBlackKey;
    $[12] = keyName;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <td className={t7}>{t8}</td>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = columns.map(column => <td key={column.property} className={cn(cellClassName, !activeFields.includes(column.property) && "opacity-60")}><MeasureInputField aria-label={t("{{measure}}, key {{number}}", {
      measure: column.label,
      number: keyNumber
    })} defaultValue={keySpec[column.property]} name={column.property} onUpdate={onUpdateKeyProperty(column.property)} placeholder={column.label} warning={column.property === "upWeight" && isUpAboveDown ? t("Up weight above down weight: probably swapped") : undefined} /></td>);
  let t11;
  if ($[16] !== keyNumber || $[17] !== t1 || $[18] !== t10 || $[19] !== t2 || $[20] !== t3 || $[21] !== t6 || $[22] !== t9) {
    t11 = <tr id={t1} data-key-number={keyNumber} aria-current={t2} className={t3}>{t6}{t9}{t10}</tr>;
    $[16] = keyNumber;
    $[17] = t1;
    $[18] = t10;
    $[19] = t2;
    $[20] = t3;
    $[21] = t6;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
});